import React, { useState, useEffect } from 'react';
import { ratesService } from '../service/ratesService';
import './MetalRatesSection.css';

const MetalRatesSection = () => {
  const [ratesData, setRatesData] = useState(ratesService.getCurrentRates());

  useEffect(() => {
    const unsub = ratesService.subscribe((rates) => {
      setRatesData(rates);
    });
    return () => unsub();
  }, []);

  const g24 = ratesData?.gold24k;
  const g22 = ratesData?.gold22k;
  const g18 = ratesData?.gold18k;
  const s999 = ratesData?.silver999;
  const s925 = ratesData?.silver925;
  const p950 = ratesData?.platinum950;

  const metalCards = [
    {
      metal: 'GOLD 24K',
      purity: '999 Fine Bullion Standard',
      rate: g24 ? g24.priceFormatted : '₹75,120',
      unit: 'per 10 grams',
      change: g24 ? `${g24.change?.formatted || ''} (${g24.change?.pct || '0.00%'})` : '+₹180 (+0.24%)',
      positive: g24 ? g24.change?.positive : true,
      badge: 'Base Bullion Rate',
      desc: 'Pure benchmark rate for 999 cast bars, mint coins, and master alloy formulations.',
      perGram: g24 ? `₹${g24.perGram}/g` : '₹7,512/g',
    },
    {
      metal: 'GOLD 22K (916)',
      purity: '91.6% Pure BIS Hallmarked',
      rate: g22 ? g22.priceFormatted : '₹68,860',
      unit: 'per 10 grams',
      change: g22 ? `${g22.change?.formatted || ''} (${g22.change?.pct || '0.00%'})` : '+₹145 (+0.21%)',
      positive: g22 ? g22.change?.positive : true,
      badge: 'Jewellery Standard',
      desc: 'The official BIS hallmark standard for retail & wholesale jewellery fabrication across Indian markets.',
      perGram: g22 ? `₹${g22.perGram}/g` : '₹6,886/g',
    },
    {
      metal: 'GOLD 18K (750)',
      purity: '75.0% Pure Gold Alloy',
      rate: g18 ? g18.priceFormatted : '₹56,340',
      unit: 'per 10 grams',
      change: g18 ? `${g18.change?.formatted || ''} (${g18.change?.pct || '0.00%'})` : '+₹110 (+0.19%)',
      positive: g18 ? g18.change?.positive : true,
      badge: 'Diamond Setting',
      desc: 'Ideal structural alloy standard for studding diamonds, precious gemstones, and lightweight CAD designs.',
      perGram: g18 ? `₹${g18.perGram}/g` : '₹5,634/g',
    },
    {
      metal: 'SILVER 999',
      purity: '99.9% Fine Bullion Silver',
      rate: s999 ? s999.priceFormatted : '₹89,850',
      unit: 'per 1 Kilogram',
      change: s999 ? `${s999.change?.formatted || ''} (${s999.change?.pct || '0.00%'})` : '-₹110 (-0.12%)',
      positive: s999 ? s999.change?.positive : false,
      badge: 'Industrial & Bullion',
      desc: 'Benchmark trading standard for silver bullion bars, wholesale payals, silverware, and temple donation articles.',
      perGram: s999 ? `₹${s999.perGram}/g` : '₹89.85/g',
    },
    {
      metal: 'SILVER 925',
      purity: '92.5% Sterling Silver',
      rate: s925 ? s925.priceFormatted : '₹831',
      unit: 'per 10 grams',
      change: s925 ? `${s925.change?.formatted || ''} (${s925.change?.pct || '0.00%'})` : '+₹4 (+0.05%)',
      positive: s925 ? s925.change?.positive : true,
      badge: 'Sterling Jewellery',
      desc: 'The global standard for designer silver fashion jewellery, oxidised ethnic sets, and exports.',
      perGram: s925 ? `₹${s925.perGram}/g` : '₹83.10/g',
    },
    {
      metal: 'PLATINUM 950',
      purity: '95.0% Pure Certified Pt',
      rate: p950 ? p950.priceFormatted : '₹32,450',
      unit: 'per 10 grams',
      change: p950 ? `${p950.change?.formatted || ''} (${p950.change?.pct || '0.00%'})` : '+₹105 (+0.32%)',
      positive: p950 ? p950.change?.positive : true,
      badge: 'PGI Certified Platinum',
      desc: 'Prestige metal standard for luxury wedding bands, solitary diamond rings, and premium men’s accessories.',
      perGram: p950 ? `₹${p950.perGram}/g` : '₹3,245/g',
    },
  ];

  return (
    <section className="metal-rates-section" id="rates">
      <div className="jmi-container">
        <div className="section-header-center">
          <div className="section-label">
            <span className="live-dot pulse-1" style={{ marginRight: '6px' }}></span>
            TRANSPARENT BULLION VALUATION 
          </div>
          <h2 className="section-title">
            Market-Linked <span className="gold-gradient-text">Pricing Architecture</span>
          </h2>
          <p className="section-subtitle">
            Every B2B quotation on JMI decouples pure metal spot value from making charges, giving buyers and sellers 
            complete mathematical transparency on every gram traded with direct SuperAdmin synchronized constraints.
          </p>
        </div>

        <div className="rates-cards-grid">
          {metalCards.map((card, idx) => (
            <div key={idx} className="rate-card-item">
              <div className="card-top-row">
                <span className="rate-metal-badge">{card.badge}</span>
                <span className={`rate-fluctuation ${card.positive ? 'pos' : 'neg'}`}>
                  {card.change}
                </span>
              </div>

              <h3 className="rate-card-title">{card.metal}</h3>
              <span className="rate-card-purity">{card.purity}</span>

              <div className="rate-display-box">
                <span className="main-rate-val">{card.rate}</span>
                <span className="main-rate-unit">{card.unit}</span>
              </div>

              <div className="rate-per-gram-badge">
                <span>Per Gram: <strong>{card.perGram}</strong></span>
              </div>

              <p className="rate-card-desc">{card.desc}</p>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default MetalRatesSection;
